// Tactical engine — pure data-driven position evaluation.
// The SAME play data drives coach view, player view and split view.

export const PITCH = { W: 90, L: 145 };
export const PLAY_COORDINATE_VERSION = 2;
export const pitchScreenX = (x) => PITCH.W - x;
export const pitchScreenY = (y) => PITCH.L - y;

// The board's left is world +x. Use the same handedness in the player camera.
export function playerViewBasis(team, yaw = 0) {
  const angle = (team === "A" ? Math.PI / 2 : -Math.PI / 2) + yaw;
  return { fwd: { x: Math.cos(angle), y: Math.sin(angle) }, right: { x: -Math.sin(angle), y: Math.cos(angle) } };
}

export function playerFacingBall(entity, ball, previous) {
  const fallback = previous || { x: 0, y: entity.team === "A" ? 1 : -1, elevation: 0 };
  if (!ball || ball.carrierId === entity.id) return fallback;
  const dx = ball.x - entity.x, dy = ball.y - entity.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 0.15) return fallback;
  const ballHeight = ball.carrierId ? 1 : ball.z ?? 0.4;
  return { x: dx / distance, y: dy / distance, elevation: Math.atan2(ballHeight - 1.7, distance) };
}

export const lerp = (a, b, f) => a + (b - a) * f;
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
// Trapezoid velocity profile: short accelerate / cruise / short decelerate (no mid-run speed spike).
export const easeInOut = (t) => {
  const a = 0.22;
  t = clamp(t, 0, 1);
  if (t < a) return (t * t) / (2 * a) / (1 - a);
  if (t > 1 - a) { const u = 1 - t; return 1 - (u * u) / (2 * a) / (1 - a); }
  return (t - a / 2) / (1 - a);
};

export function pathLength(points) {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  return d;
}

function pathAt(points, f) {
  if (!points.length) return { x: 0, y: 0 };
  if (points.length === 1) return { ...points[0] };
  const seg = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const d = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    seg.push(d);
    total += d;
  }
  if (total === 0) return { ...points[0] };
  let dist = f * total;
  for (let i = 0; i < seg.length; i++) {
    if (dist <= seg[i] || i === seg.length - 1) {
      const t = seg[i] === 0 ? 0 : dist / seg[i];
      return { x: lerp(points[i].x, points[i + 1].x, t), y: lerp(points[i].y, points[i + 1].y, t) };
    }
    dist -= seg[i];
  }
  return { ...points[points.length - 1] };
}

export function playerPosAt(entity, events, t) {
  const runs = events
    .filter((e) => e.kind === "move" && e.playerId === entity.id)
    .sort((a, b) => a.startTime - b.startTime);
  let pos = { x: entity.x, y: entity.y };
  for (const r of runs) {
    const wps = r.waypoints || [];
    if (!wps.length) continue;
    if (t >= r.endTime) {
      pos = { ...wps[wps.length - 1] };
      continue;
    }
    if (t <= r.startTime) return pos;
    const f = easeInOut((t - r.startTime) / (r.endTime - r.startTime));
    return pathAt([pos, ...wps], f);
  }
  return pos;
}

export function ballStateAt(play, t) {
  const events = play.events || [];
  const byId = {};
  (play.entities || []).forEach((e) => (byId[e.id] = e));
  const ballEvents = events.filter((e) => e.kind === "ball").sort((a, b) => a.startTime - b.startTime);
  let carrier = play.ball?.holderId;
  let settledBall = null;

  const carried = (cid) => {
    const c = byId[cid];
    const p = c ? playerPosAt(c, events, t) : { x: play.ball?.x ?? 45, y: play.ball?.y ?? 6 };
    return { x: p.x, y: p.y, z: 0.4, carrierId: cid, inFlight: false };
  };

  for (const be of ballEvents) {
    if (t < be.startTime) return settledBall || carried(carrier);
    const from = byId[be.playerId];
    const to = byId[be.targetPlayerId];
    const fp = from ? playerPosAt(from, events, be.startTime) : settledBall || { x: play.ball?.x ?? 45, y: play.ball?.y ?? 6 };
    const destination = be.target || (to ? playerPosAt(to, events, be.endTime) : fp);
    if (t < be.endTime) {
      const f = easeInOut((t - be.startTime) / (be.endTime - be.startTime));
      const arc = be.ballType === "handpass" ? 0.8 : be.ballType === "kickout" ? 11 : be.ballType === "kick" ? 7 : be.ballType === "shot" ? 6 : 0.2;
      const z = arc * Math.sin(Math.PI * f) + lerp(0.4, destination.z ?? 0.4, f);
      return { x: lerp(fp.x, destination.x, f), y: lerp(fp.y, destination.y, f), z, carrierId: null, inFlight: true, from: fp, to: destination };
    }
    carrier = be.targetPlayerId || null;
    settledBall = be.target ? { ...destination, z: destination.z ?? 0.4, carrierId: null, inFlight: false, shot: be.ballType === "shot" } : null;
  }
  return settledBall || carried(carrier);
}

export function computeFrame(play, t) {
  const events = play.events || [];
  const entities = (play.entities || []).map((e) => {
    const p = playerPosAt(e, events, t);
    return { ...e, x: p.x, y: p.y };
  });
  const ball = ballStateAt(play, t);
  return { entities, ball };
}

// Full drawn path for an entity (start + all waypoints across all its move events).
export function entityPath(entity, events) {
  const runs = events
    .filter((e) => e.kind === "move" && e.playerId === entity.id)
    .sort((a, b) => a.startTime - b.startTime);
  const pts = [{ x: entity.x, y: entity.y }];
  runs.forEach((r) => (r.waypoints || []).forEach((w) => pts.push({ x: w.x, y: w.y })));
  return pts;
}

// ---------- Stages: sequential blocks of time a play is built in ----------
export function getStages(play) {
  const st = play?.stages;
  if (Array.isArray(st) && st.length) return st;
  return [{ id: "s1", name: "Stage 1", duration: play?.duration || 6 }];
}

export function stageBounds(play) {
  let acc = 0;
  return getStages(play).map((s, i) => {
    const start = acc;
    acc += s.duration;
    return { ...s, index: i, start, end: acc, name: s.name || `Stage ${i + 1}` };
  });
}

export function stageIndexAt(play, t) {
  const b = stageBounds(play);
  for (let i = 0; i < b.length; i++) if (t < b[i].end - 1e-6) return i;
  return b.length - 1;
}

export function totalDuration(play) {
  return getStages(play).reduce((a, s) => a + s.duration, 0);
}

export function displayName(play, id) {
  if (id === "ball") return "Ball";
  const e = (play?.entities || []).find((x) => x.id === id);
  if (!e) return id || "";
  return `${e.team === "B" ? "Opp " : ""}#${e.number} ${e.name || ""}`.trim();
}

// Ramer–Douglas–Peucker: reduce a drawn finger path to its key bends.
export function simplifyPath(points, eps = 1.2) {
  if (points.length <= 2) return points.slice();
  const perp = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
    if (len === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    return Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / len;
  };
  const rdp = (pts) => {
    let maxD = 0, idx = 0;
    for (let i = 1; i < pts.length - 1; i++) { const d = perp(pts[i], pts[0], pts[pts.length - 1]); if (d > maxD) { maxD = d; idx = i; } }
    if (maxD > eps) return [...rdp(pts.slice(0, idx + 1)).slice(0, -1), ...rdp(pts.slice(idx))];
    return [pts[0], pts[pts.length - 1]];
  };
  return rdp(points);
}

export const FORMATIONS = {
  "1-3-3-2-3-3": [[45,6],[74,28],[45,24],[16,28],[74,54],[45,54],[16,54],[62,79],[28,79],[74,106],[45,106],[16,106],[74,130],[45,130],[16,130]],
};

// The 15 GAA starting positions, in jersey-number order (1..15). Own goal at y=0, attacking
// up the pitch; "Right" positions are on the players' right as they face the opposition goal.
export const POSITION_SLOTS = [
  { label: "Goalkeeper", short: "GK", x: 45, y: 6 },
  { label: "Right Corner-Back", short: "RCB", x: 74, y: 28 },
  { label: "Full-Back", short: "FB", x: 45, y: 24 },
  { label: "Left Corner-Back", short: "LCB", x: 16, y: 28 },
  { label: "Right Half-Back", short: "RHB", x: 74, y: 54 },
  { label: "Centre Half-Back", short: "CHB", x: 45, y: 54 },
  { label: "Left Half-Back", short: "LHB", x: 16, y: 54 },
  { label: "Midfielder", short: "MF", x: 62, y: 79 },
  { label: "Midfielder", short: "MF", x: 28, y: 79 },
  { label: "Right Half-Forward", short: "RHF", x: 74, y: 106 },
  { label: "Centre Half-Forward", short: "CHF", x: 45, y: 106 },
  { label: "Left Half-Forward", short: "LHF", x: 16, y: 106 },
  { label: "Right Corner-Forward", short: "RCF", x: 74, y: 130 },
  { label: "Full-Forward", short: "FF", x: 45, y: 130 },
  { label: "Left Corner-Forward", short: "LCF", x: 16, y: 130 },
];

// Persisted before the formation convention changed. Require a strong geometry match:
// missing version alone is not evidence, because newer saved plays also lack it.
const LEGACY_STARTING_POSITIONS = [[45,6],[22,18],[45,15],[68,18],[22,40],[45,37],[68,40],[36,62],[54,62],[22,88],[45,90],[68,88],[24,116],[45,120],[66,116]];

export function migratePlayCoordinates(play) {
  if (play.coordinateVersion >= PLAY_COORDINATE_VERSION) return { play, corrected: false };
  const matches = new Set((play.entities || []).filter((e) => {
    const slotId = e.team === "A" && /^A([1-9]|1[0-5])$/.exec(e.id);
    if (!slotId) return false;
    const [x, y] = LEGACY_STARTING_POSITIONS[Number(slotId[1]) - 1];
    return Math.abs(e.x - x) < 0.25 && Math.abs(e.y - y) < 0.25;
  }).map((e) => e.id));
  const versioned = { ...play, coordinateVersion: PLAY_COORDINATE_VERSION };
  if (matches.size < 10) return { play: versioned, corrected: false };

  const mirror = (point) => {
    const result = { ...point };
    for (const key of ["x", "x1", "x2"]) {
      if (Number.isFinite(point[key])) result[key] = PITCH.W - point[key];
    }
    return result;
  };
  return {
    corrected: true,
    play: {
      ...versioned,
      entities: play.entities.map(mirror),
      ball: play.ball ? mirror(play.ball) : play.ball,
      events: (play.events || []).map((event) => Array.isArray(event.waypoints)
        ? { ...event, waypoints: event.waypoints.map(mirror) } : event),
      annotations: (play.annotations || []).map(mirror),
    },
  };
}

// Squad numbers identify roster records; the array index determines the match jersey/position.
export function getStartingLineup(team) {
  const squad = team.squad || [];
  const numbers = team.startingXV?.length ? team.startingXV : squad.slice(0, 15).map((p) => p.number);
  return POSITION_SLOTS.map((_, i) => numbers[i] == null ? null : squad.find((p) => String(p.number) === String(numbers[i])) || null);
}

// Opposition marker standing shoulder-to-shoulder with the player in a slot.
export function markerFor(slot, index = -1) {
  if (index === 0 || slot.short === "GK") return { x: 45, y: 139 }; // opposition keeper in the far goal
  return { x: clamp(slot.x + (slot.x <= 45 ? 6.5 : -6.5), 3, 87), y: clamp(slot.y + 4, 3, 142) };
}

// In GAA opposing jerseys add up to 17 (3 marks 14, 2 marks 15, 8↔9 …). Keepers (#1) stay in goal.
export function opponentNumberFor(homeNumber) {
  const n = 17 - homeNumber;
  return n >= 1 && n <= 15 ? n : null;
}

// Standard starting shape: home by jersey slot; each opposition player stands beside the
// home player they mark (#(17-n)); both keepers sit in their own goal.
export function assignStandardPositions(entities) {
  const pos = {};
  (entities || []).filter((e) => e.team === "A").forEach((h) => {
    const s = POSITION_SLOTS[h.number - 1];
    if (s) pos[h.id] = { x: s.x, y: s.y, position: s.label };
  });
  (entities || []).filter((e) => e.team === "B").forEach((o) => {
    const own = POSITION_SLOTS[o.number - 1];
    if (o.number === 1) { pos[o.id] = { x: 45, y: 139, position: own ? own.label : "Goalkeeper" }; return; }
    const si = 16 - o.number; // slot of the home player #(17-n) this opponent marks
    const slot = POSITION_SLOTS[si];
    if (slot) pos[o.id] = { ...markerFor(slot, si), position: own ? own.label : slot.label };
  });
  return pos;
}

// Official GAA pitch markings (metres from our own end line). Used by both views.
export const PITCH_LINES = [13, 20, 45];
export const D_RADIUS = 13;
export const LARGE_RECT = { w: 19, d: 13 };
export const SMALL_RECT = { w: 14, d: 4.5 };

export function zoneOpacity(zone, entities) {
  const team = zone.team || "A";
  let nearest = Infinity;
  (entities || []).forEach((e) => {
    if (e.team === team) nearest = Math.min(nearest, Math.hypot(e.x - zone.x, e.y - zone.y));
  });
  const r = zone.r || 10;
  const hi = r * 1.6, lo = r * 0.35;
  const f = clamp((nearest - lo) / (hi - lo), 0, 1);
  return 0.05 + f * 0.3;
}

function hexToHsl(hex) {
  let h = String(hex).replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = (parseInt(h.slice(0, 2), 16) || 0) / 255;
  const g = (parseInt(h.slice(2, 4), 16) || 0) / 255;
  const b = (parseInt(h.slice(4, 6), 16) || 0) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hh = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hh = (b - r) / d + 2;
    else hh = (r - g) / d + 4;
    hh *= 60;
  }
  return { h: hh, s, l };
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Returns a strongly-contrasting opponent colour (complementary hue) for any team colour.
export function opponentColor(primary) {
  const { h, s, l } = hexToHsl(primary || "#0EA5E9");
  const oh = (h + 180) % 360;
  const os = Math.max(0.6, s);
  const ol = l > 0.6 ? 0.45 : l < 0.35 ? 0.56 : 0.5;
  return hslToHex(oh, os, ol);
}
